import { useState, useEffect, useCallback } from 'react';
import { useRestaurant } from '@core/tenant/TenantProvider';
import { env } from '@core/config/env';

/**
 * MercadoPago SDK v1 global type declarations.
 */
declare global {
  interface Window {
    Mercadopago: {
      setPublishableKey: (key: string) => void;
      clearSession: () => void;
      createToken: (form: HTMLFormElement, callback: (status: number, response: MercadoPagoTokenResponse) => void) => void;
      getPaymentMethod: (params: { bin: string }, callback: (status: number, response: MercadoPagoPaymentMethod[]) => void) => void;
      key?: string;
    };
  }
}

interface MercadoPagoTokenResponse {
  id?: string;
  error?: string;
  cause?: unknown[];
}

interface MercadoPagoPaymentMethod {
  id: string;
  name: string;
  payment_type_id: string;
}

const MP_SDK_URL = 'https://secure.mlstatic.com/sdk/javascript/v1/mercadopago.js';
const MP_SCRIPT_ID = 'mercadopago-sdk-script';
const POLL_INTERVAL = 250;
const TIMEOUT_MS = 15000;

interface UseMercadoPagoSDKResult {
  isReady: boolean;
  error: string | null;
  createToken: (form: HTMLFormElement) => Promise<string>;
  getPaymentMethod: (bin: string) => Promise<string>;
}

export function useMercadoPagoSDK(): UseMercadoPagoSDKResult {
  const restaurant = useRestaurant();
  const isMP = restaurant.paymentProcessor === 'mercadopago';
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isMP) return;

    setIsReady(false);
    setError(null);

    // Don't load if already present
    if (!document.getElementById(MP_SCRIPT_ID)) {
      const script = document.createElement('script');
      script.src = MP_SDK_URL;
      script.id = MP_SCRIPT_ID;
      script.async = true;
      document.body.appendChild(script);
    }

    const startTime = Date.now();
    const poll = setInterval(() => {
      if (typeof window.Mercadopago !== 'undefined') {
        clearInterval(poll);
        // Use marketplace key if restaurant has marketplace, else restaurant's own key
        const key = restaurant.hasMarketplace
          ? env.mpPublishableKey
          : restaurant.publicKeyPayment;
        window.Mercadopago.setPublishableKey(key);
        setIsReady(true);
      } else if (Date.now() - startTime > TIMEOUT_MS) {
        clearInterval(poll);
        setError('MercadoPago SDK failed to load');
      }
    }, POLL_INTERVAL);

    return () => {
      clearInterval(poll);
      setIsReady(false);
      setError(null);
    };
  }, [isMP, restaurant.hasMarketplace, restaurant.publicKeyPayment]);

  const createToken = useCallback((form: HTMLFormElement): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (!window.Mercadopago) {
        reject(new Error('MercadoPago SDK not loaded'));
        return;
      }
      window.Mercadopago.clearSession();
      window.Mercadopago.createToken(form, (status, response) => {
        if ((status === 200 || status === 201) && response.id) {
          resolve(response.id);
        } else {
          reject(new Error(response.error || 'Failed to create MercadoPago token'));
        }
      });
    });
  }, []);

  const getPaymentMethod = useCallback((bin: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (!window.Mercadopago) {
        reject(new Error('MercadoPago SDK not loaded'));
        return;
      }
      window.Mercadopago.getPaymentMethod({ bin }, (status, response) => {
        if (status === 200 && response.length > 0) {
          resolve(response[0].id);
        } else {
          reject(new Error('Failed to detect payment method'));
        }
      });
    });
  }, []);

  return { isReady, error, createToken, getPaymentMethod };
}
